"use client";
import React from "react";
import Badge from "@/components/primitives/badge";
import Avatar from "@/components/primitives/avatar";
import Icon from "@/components/primitives/icon";
import * as Collapsible from "@radix-ui/react-collapsible";
import GameComment from "../game/comment";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import CommentForm from "../game/comment-form";
import { DiscussionComment, GameDiscussion } from "common";
import NotFound from "../primitives/not-found";
import { useWebSocketMessage } from "@/utils/useWebSocketMessage";

export default function DiscussionBoard({
  gameId,
  data: initialData,
  open,
}: {
  gameId: number;
  data: GameDiscussion;
  open: boolean;
}) {
  const [data, setData] = React.useState<GameDiscussion>(initialData);
  const wsDiscussion = useWebSocketMessage("discussion");
  const commentContainerRef = React.useRef<HTMLDivElement>(null);

  function scrollToBottom() {
    if (commentContainerRef.current) {
      commentContainerRef.current.scrollTop =
      commentContainerRef.current.scrollHeight -
      commentContainerRef.current.clientHeight;
    }
  }

  // scroll to the bottom on mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // React.useEffect(() => {
  //   scrollToBottom();
  // }, [data]);
  // console.log("new comment", wsDiscussion);

  React.useEffect(() => {
    console.log("new comment", wsDiscussion);
  
    const insertComment = (
      comments: DiscussionComment[],
      commentToInsert: DiscussionComment,
      parentId: number,
      keyLast?: number
    ): DiscussionComment[] => {
      return comments.map(comment => {
        if (comment.id === parentId) {
          const newChildren = comment.children ? [...comment.children] : [];
          const insertIndex = keyLast
            ? newChildren.findIndex(c => c.id === keyLast) + 1
            : 0;
  
          if (insertIndex >= 0 && insertIndex <= newChildren.length) {
            newChildren.splice(insertIndex, 0, commentToInsert);
          } else {
            newChildren.push(commentToInsert);
          }
  
          return {
            ...comment,
            children: newChildren,
          };
        }
  
        return {
          ...comment,
          children: comment.children
            ? insertComment(comment.children, commentToInsert, parentId, keyLast)
            : comment.children,
        };
      });
    };
  
    if (wsDiscussion?.type === "commented" && wsDiscussion.comment && wsDiscussion.comment.gameID === gameId) {
      setData(prevData => {
        const { parentCommentID } = wsDiscussion.comment;
        const newComments = parentCommentID
          ? insertComment(prevData.comments, wsDiscussion.comment, parentCommentID, wsDiscussion.key.last)
          : [...prevData.comments, wsDiscussion.comment as DiscussionComment];
  
        return {
          ...prevData,
          comments: newComments,
          totalComments: prevData.totalComments + 1,
        };
      });
    }
  }, [wsDiscussion, gameId]);
  
  
  
  return (
    <Collapsible.Root defaultOpen={open ? true : false} className="group overflow-hidden shadow-card">
      <Collapsible.Trigger asChild>
        <button className="relative z-[1] h-[56px] w-full py-0 px-4 rounded-xl flex items-center flex-row gap-4 justify-between bg-secondary-300">
          {/* <div className="absolute right-4 top-0 bottom-0 h-full flex items-center justify-center">

          </div> */}

          <div className="flex flex-row gap-x-4 w-full justify-between items-center">
            <div className="flex flex-row gap-4 items-center whitespace-nowrap min-w-[280px]">
              {open ? (

                <img src="/images/discussion/discussion-open.png" alt="" className="h-12 -ml-2 -mr-2" />
              ) : (
                <img src="/images/discussion/discussion-closed.png" alt="" className="h-8" />

              )}

              <div className="hidden desktop-grid:block text-headline-bold uppercase text-surface-100">
                {open ? "Join the Discussion" : !open && data.comments.length ? "View the Discussion" : ""}
              </div>
              <div className="flex flex-row gap-4 items-center w-full desktop-grid:justify-between pr-8">
                <Badge
                  color="white"
                  value={data.totalComments}
                  showZero={true}
                  icon={
                    data.totalComments && data.totalComments > 0
                      ? { name: "flame", gradient: "flame" }
                      : undefined
                  }
                  label={`Comment${data.totalComments != 1 ? "s" : ""}`}
                />
                <Avatar.Group
                  avatars={data.avatars}
                  value={data.totalBettors}
                  label="Bettors"
                  side="right"
                  dark
                />
              </div>
            </div>
            <Icon name="caret-down" className="fill-white"></Icon>
          </div>
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content className="data-[state=open]:animate-unCollapse data-[state=closed]:animate-collapse bg-white relative z-0 rounded-bl-lg rounded-br-lg">
        <div className="absolute -top-8 bg-white w-full h-8"></div>
        <div className="overflow-hidden">

        <ScrollArea.Root>
          <ScrollArea.Viewport ref={commentContainerRef} className="w-full h-full">
            <div className="min-h-[6.5rem] max-h-[26.5rem] p-5">
              {data.comments.length ? (
                <>
                  {data.comments.map((comment, i) => (
                    <GameComment gameId={gameId} data={comment} key={comment.id} disabled={!open}/>
                  ))}
                </>
              ) : open && (
                <div className="pt-8">
                  <NotFound label="Be the first to comment" />
                </div>
              )}
            </div>
            {/* <div ref={commentsEndRef}></div> */}
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            className="flex select-none touch-none p-0.5 bg-light transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
            orientation="vertical"
          >
            <ScrollArea.Thumb className="flex-1 bg-secondary rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
        </div>
            
        {open && (
          <div className="bg-white p-6 flex flex-row gap-2 items-center rounded-br-lg rounded-bl-lg">
          <CommentForm gameId={gameId} disabled={!open} />
        </div>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
